import { useState } from 'react';
import { SuggestedActionsPanel } from '@/components/ai-suggestions';
import { EmailDraftSheet } from '@/components/ai-suggestions';
import type { SuggestedActionWithStatus } from '@/components/ai-suggestions';
import {
  useApproveAction,
  useRejectAction,
} from '@/api/rpc/hooks/ai/useSuggestedActions';
import {
  useEmailDraft,
  useSendEmailDraft,
} from '@/api/rpc/hooks/communications/useEmailDraft';
import type { SuggestedActionResponse, EmailDraftSummary } from '@erp/rpc';

interface AIPanelProps {
  entityType: string;
  entityId: string;
  conversationId?: string | null;
  actions: SuggestedActionResponse[];
  emailDraft: EmailDraftSummary | null;
}

export function AIPanel({
  entityType,
  entityId,
  actions,
  emailDraft,
}: AIPanelProps) {
  const [approvingActionId, setApprovingActionId] = useState<string | null>(
    null,
  );
  const [isEmailDraftOpen, setIsEmailDraftOpen] = useState(false);

  const approveAction = useApproveAction();
  const rejectAction = useRejectAction();

  // Fetch full draft content when the sheet is open
  const { draft: fullDraft } = useEmailDraft(emailDraft?.id, {
    enabled: isEmailDraftOpen && !!emailDraft?.id,
  });
  const { updateAndSend, isPending: isSending } = useSendEmailDraft();

  function handleApprove(action: SuggestedActionWithStatus) {
    setApprovingActionId(action.id);
    approveAction.mutate(
      { actionId: action.id },
      {
        onSettled: () => setApprovingActionId(null),
      },
    );
  }

  function handleReject(action: SuggestedActionWithStatus) {
    rejectAction.mutate({ actionId: action.id });
  }

  function handleDismiss(action: SuggestedActionWithStatus) {
    rejectAction.mutate({ actionId: action.id });
  }

  function handleRerun() {
    // TODO: wire to real re-run agent endpoint
    console.log('Re-run agent for entity:', entityType, entityId);
  }

  function handleReviewDraft() {
    setIsEmailDraftOpen(true);
  }

  async function handleSendDraft(edited: {
    to: string;
    cc?: string;
    subject: string;
    body: string;
  }) {
    if (!emailDraft?.id) return;

    const htmlContent = `<p>${edited.body.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;

    await updateAndSend(emailDraft.id, {
      to: edited.to,
      cc: edited.cc,
      subject: edited.subject,
      htmlContent,
    });

    setIsEmailDraftOpen(false);
  }

  // Use the full draft data for the sheet if available, fall back to summary
  const draftForSheet = fullDraft
    ? {
        to: fullDraft.to,
        cc: fullDraft.cc ?? undefined,
        subject: fullDraft.subject,
        body: fullDraft.body,
      }
    : emailDraft
      ? {
          to: emailDraft.to,
          cc: emailDraft.cc ?? undefined,
          subject: emailDraft.subject,
          body: emailDraft.body,
        }
      : null;

  return (
    <>
      <SuggestedActionsPanel
        actions={actions}
        emailDraft={emailDraft}
        onApprove={handleApprove}
        onReject={handleReject}
        onDismiss={handleDismiss}
        onRerun={handleRerun}
        onReviewDraft={handleReviewDraft}
        onDismissAll={() => {
          for (const action of actions) {
            if (action.status === 'pending') {
              rejectAction.mutate({ actionId: action.id });
            }
          }
        }}
        approvingActionId={approvingActionId}
        className="max-h-full"
      />

      {draftForSheet && (
        <EmailDraftSheet
          draft={draftForSheet}
          open={isEmailDraftOpen}
          onOpenChange={setIsEmailDraftOpen}
          onSend={handleSendDraft}
          isSending={isSending}
        />
      )}
    </>
  );
}
